import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Globe, LineChart, FileText, Users, Target, TrendingDown } from 'lucide-react';

const ClimateStrategy = () => {
  const services = [
    {
      icon: <Globe size={32} />,
      title: 'Policy & Compliance Strategy',
      description: 'Navigate Kigali Amendment, Paris Agreement, EU Green Deal, and national regulations with expert guidance.'
    },
    {
      icon: <LineChart size={32} />,
      title: 'Emissions Modeling & Forecasting',
      description: 'AI-powered scenario analysis to project emissions, evaluate pathways, and optimize abatement strategies.'
    },
    {
      icon: <FileText size={32} />,
      title: 'Roadmap Development',
      description: 'Comprehensive decarbonization plans with sector-specific timelines, KPIs, and governance frameworks.'
    },
    {
      icon: <Target size={32} />,
      title: 'Portfolio Optimization',
      description: 'Identify and prioritize high-impact projects using AI-driven cost-benefit and risk analysis.'
    },
    {
      icon: <TrendingDown size={32} />,
      title: 'HFC Phase-Down Programs',
      description: 'Specialized support for refrigerant transition, HVAC upgrades, and F-gas compliance.'
    },
    {
      icon: <Users size={32} />,
      title: 'Stakeholder Engagement',
      description: 'Facilitate alignment between industry, government, and civil society for climate action.'
    }
  ];

  const clientTypes = [
    {
      type: 'Governments & Regulators',
      description: 'National and regional climate policy development, treaty compliance monitoring, and cross-border coordination.'
    },
    {
      type: 'Large Corporates',
      description: 'Enterprise-wide emissions reduction strategies, Scope 1-3 tracking, and ESG reporting automation.'
    },
    {
      type: 'Climate-Intensive Sectors',
      description: 'Industry-specific solutions for manufacturing, energy, transport, agriculture, and built environment.'
    }
  ];

  const frameworks = [
    'Paris Agreement',
    'Kigali Amendment',
    'EU Green Deal',
    'Fit for 55 Package',
    'F-Gas Regulation',
    'National Climate Laws',
    'GHG Protocol',
    'TCFD & CDP Reporting'
  ];

  return (
    <div style={{ paddingTop: '80px', background: 'var(--bg-primary)' }}>
      {/* Hero */}
      <section style={{
        padding: '100px 7.6923%',
        background: 'var(--bg-primary)',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <h1 className="display-huge" style={{ marginBottom: '24px' }}>
            HFC & CO₂ Reduction Strategy
          </h1>
          <p className="body-large" style={{ marginBottom: '40px', color: 'var(--text-secondary)' }}>
            Expert climate consultancy powered by AI modeling. We help governments and corporations achieve treaty compliance, reduce emissions, and turn climate commitments into measurable action.
          </p>
          <Link to="/contact" className="btn-primary">
            Request a decarbonization workshop
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* How AI Amplifies Our Consulting */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 className="display-medium" style={{ marginBottom: '40px', textAlign: 'center' }}>
            How AI Amplifies Our Consulting
          </h2>
          <div style={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-subtle)',
            padding: '40px'
          }}>
            <p className="body-large" style={{ marginBottom: '32px', color: 'var(--text-secondary)' }}>
              Traditional climate consulting relies on spreadsheets and manual analysis that take weeks. We use AI to evaluate hundreds of scenarios in hours, enabling faster, better-informed decisions.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {[
                'Scenario modeling: Simulate policy pathways and technology adoption curves',
                'Abatement cost curves: Optimize investment priorities by €/ton CO₂',
                'Forecasting: Predict emissions trajectories under different assumptions',
                'Impact simulation: Model economic and social effects of climate policies',
                'Real-time dashboards: Executive-ready visualizations and automated reporting'
              ].map((feature, i) => (
                <li key={i} style={{
                  fontSize: '18px',
                  color: 'var(--text-secondary)',
                  marginBottom: '16px',
                  paddingLeft: '32px',
                  position: 'relative'
                }}>
                  <span style={{
                    position: 'absolute',
                    left: 0,
                    color: 'var(--brand-primary)',
                    fontSize: '20px'
                  }}>✓</span>
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What We Deliver */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            What We Deliver
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '40px'
          }}>
            {services.map((service, idx) => (
              <div
                key={idx}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)',
                  padding: '32px',
                  transition: 'all 0.4s ease'
                }}
                className="dark-hover"
              >
                <div style={{ color: 'var(--brand-primary)', marginBottom: '16px' }}>
                  {service.icon}
                </div>
                <h3 className="heading-3" style={{ marginBottom: '12px' }}>
                  {service.title}
                </h3>
                <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Advise */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-secondary)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
            Who We Advise
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
            gap: '40px'
          }}>
            {clientTypes.map((client, idx) => (
              <div
                key={idx}
                style={{
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-subtle)',
                  padding: '40px'
                }}
              >
                <h3 className="heading-2" style={{ marginBottom: '16px', color: 'var(--brand-primary)' }}>
                  {client.type}
                </h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                  {client.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Treaties & Frameworks */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-primary)' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
          <h2 className="display-medium" style={{ marginBottom: '40px' }}>
            Treaties & Frameworks We Work With
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '24px',
            marginTop: '40px'
          }}>
            {frameworks.map((framework, idx) => (
              <div
                key={idx}
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)',
                  padding: '20px',
                  fontSize: '16px',
                  color: 'var(--text-primary)',
                  fontWeight: 500,
                  transition: 'all 0.3s ease'
                }}
                className="dark-hover"
              >
                {framework}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '100px 7.6923%', background: 'var(--bg-secondary)', textAlign: 'center' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 className="display-large" style={{ marginBottom: '24px' }}>
            Turn commitments into action
          </h2>
          <p className="body-large" style={{ marginBottom: '40px', color: 'var(--text-secondary)' }}>
            Let's discuss your climate goals and how we can help you achieve them.
          </p>
          <Link to="/contact" className="btn-primary">
            Book a consultation
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ClimateStrategy;
